import React, { useEffect, useMemo, useState } from 'react';

import { useFunnelsDropdown, useFunnelStepsDropdown } from '@/modules/funnels';
import { Button, Modal, ModalContent, ModalFooter, ModalHeader, Select } from '@/shared/components/ui';

/**
 * Props do BulkChangeFunnelModal
 */
export interface BulkChangeFunnelModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Quantos leads o lote vai atingir — vai no título, é o que dá a dimensão. */
  affectedCount: number;
  /** Aplicar. O par funil/etapa vem sempre completo. */
  onConfirm: (funnelId: string, stepId: string) => void;
  isPending?: boolean;
}

/**
 * Mini-modal "Mover para funil/etapa" da barra de ações em massa dos leads.
 *
 * Espelha o modal "Alterar nível" da tela de usuários: um seletor, um
 * confirmar. A diferença é que aqui são DOIS seletores encadeados — etapa só
 * existe dentro de um funil, e o backend recusa o par inconsistente.
 *
 * As etapas são buscadas por `/funnel-steps/by-funnel/:funnelId` (rota sem
 * permissão de módulo — NUNCA #15) e só depois de o funil ser escolhido
 * (lazy fetching — SEMPRE #10).
 */
export const BulkChangeFunnelModal: React.FC<BulkChangeFunnelModalProps> = ({
  isOpen,
  onClose,
  affectedCount,
  onConfirm,
  isPending = false,
}) => {
  const [funnelId, setFunnelId] = useState('');
  const [stepId, setStepId] = useState('');

  // Hooks de fetch condicionados à abertura: rodam ANTES do early-return de
  // `isOpen`, então sem o gate a tela buscaria funis o tempo todo (NUNCA #14).
  const { data: funnels } = useFunnelsDropdown({ enabled: isOpen });
  const { data: steps, isLoading: isLoadingSteps } = useFunnelStepsDropdown(
    isOpen && funnelId ? funnelId : undefined,
  );

  // Reabrir é recomeçar — manter a escolha anterior convida a aplicar o
  // destino errado numa seleção completamente diferente.
  useEffect(() => {
    if (isOpen) {
      setFunnelId('');
      setStepId('');
    }
  }, [isOpen]);

  const funnelOptions = useMemo(
    () => (funnels ?? []).map((funnel) => ({ value: funnel.id, label: funnel.name })),
    [funnels],
  );

  const stepOptions = useMemo(
    () => [...(steps ?? [])]
      .sort((a, b) => a.order - b.order)
      .map((step) => ({ value: step.id, label: step.name })),
    [steps],
  );

  if (!isOpen) return null;

  const stepPlaceholder = !funnelId
    ? 'Escolha o funil primeiro'
    : isLoadingSteps
      ? 'Carregando etapas...'
      : stepOptions.length === 0
        ? 'Este funil não tem etapas'
        : 'Selecione a etapa';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader onClose={onClose}>
        {`Mover ${affectedCount} lead(s) de funil`}
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4" data-testid="leads-bulk-funnel-modal">
          <Select
            label="Funil de destino"
            value={funnelId}
            onChange={(value) => {
              setFunnelId(value);
              // A etapa pertence ao funil anterior — mantê-la mandaria um par
              // inconsistente, que o backend recusa com 400.
              setStepId('');
            }}
            options={funnelOptions}
            placeholder="Selecione o funil"
            fullWidth
            data-testid="leads-bulk-funnel-select"
          />
          <Select
            label="Etapa de destino"
            value={stepId}
            onChange={setStepId}
            options={stepOptions}
            placeholder={stepPlaceholder}
            disabled={!funnelId || isLoadingSteps || stepOptions.length === 0}
            fullWidth
            data-testid="leads-bulk-step-select"
          />
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Mover em massa não dispara automações de mudança de etapa.
          </p>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          onClick={() => onConfirm(funnelId, stepId)}
          disabled={!funnelId || !stepId || isPending}
          data-testid="leads-bulk-funnel-confirm"
        >
          Mover
        </Button>
      </ModalFooter>
    </Modal>
  );
};
